/**
 * The ry-ui data contract — types only, no transport.
 *
 * Authoritative spec: docs/arch/data-contract.md (ratified 2026-08-17).
 *
 * Two rules this file exists to enforce by construction:
 *
 *   1. NO DOM TYPES. Nothing here may reference Element, Node, Event, or
 *      HTMLElement. A contract carrying signal/range/sort/filter/rows has no
 *      reason to mention the DOM, so keeping it out costs nothing.
 *      `AbortSignal` is fine — a platform-neutral standard present in every
 *      JS runtime.
 *
 *   2. NO TRANSPORT. This module never calls fetch(). Transport belongs to the
 *      source function the consumer supplies, the same way positioning belongs
 *      to core/ry-floating.ts and animation to core/ry-motion.ts.
 */

/** A record. Widgets read fields out of this by name; `id` should be stable. */
export type RyRecord = Record<string, unknown>;

/** Sort direction for a single column. */
export type RySortDir = 'asc' | 'desc';

/** One column's contribution to a multi-column sort. */
export interface RySortSpec {
  field: string;
  dir: RySortDir;
}

/**
 * Half-open window of records wanted: `[start, end)`.
 *
 * The WIDGET speaks pages (`ry-pagination` keeps its `page` / `total` surface);
 * the CONTRACT speaks ranges. One window primitive covers pagination and
 * virtualization, so both hit the same backend adapter unchanged.
 */
export interface RyRange {
  start: number;
  end: number;
}

export interface RyRequest {
  /**
   * Sources MUST forward this to fetch(). Without it, overlapping requests
   * (typing in a filter box) can land out of order and render stale rows that
   * look correct — unfixable at the widget layer.
   */
  signal: AbortSignal;
  range?: RyRange;
  /** Most significant column first. */
  sort?: RySortSpec[];
  /** Field-keyed filters. Values are widget-defined; the source interprets them. */
  filter?: Record<string, unknown>;
  /** Free-text search, when the widget has a search affordance. */
  query?: string;
  /**
   * Opaque cursor from the PREVIOUS response, asking for what comes after it.
   *
   * Cursor and offset pagination are both supported and are mutually exclusive
   * per request: `range` says "records 25–50", `cursor` says "whatever follows
   * this". A widget picks its mode from what the source returns (see
   * `RyResponse.cursor`) and sticks to it.
   */
  cursor?: string;
  /** Opaque pass-through, copied from [source-meta]. The widget never reads it. */
  meta?: Record<string, unknown>;
}

export interface RyResponse {
  rows: ReadonlyArray<RyRecord>;
  /**
   * Total matching records. Drives page counts and scrollbar sizing.
   * Omit when unknown — which is the normal case for cursor pagination.
   */
  total?: number;
  /**
   * Opaque cursor pointing PAST the last row in this response. Send it back as
   * `RyRequest.cursor` to get the next page.
   *
   * Its ABSENCE is the end-of-results signal — there is no separate `hasMore`
   * flag, because two ways to say the same thing is two ways to disagree.
   * Offset-paginated sources simply never set it.
   */
  cursor?: string;
}

/**
 * One piece of a streamed response.
 *
 * `append` is what makes LLM chat work without every app reimplementing token
 * gluing: the delta extends a field on the LAST row rather than creating one.
 * `add` is for streams whose chunks ARE rows (logs). `done` closes the stream.
 */
export interface RyChunk {
  op: 'add' | 'append' | 'done';
  /** 'add': the new record. */
  row?: RyRecord;
  /** 'append': which field of the last row the delta extends. */
  field?: string;
  /** 'append': the text to append. */
  delta?: string;
}

/** A live connection. `unsubscribe` MUST be safe to call twice. */
export interface RySubscription {
  unsubscribe(): void;
}

/**
 * A row-level change pushed by a subscription. Row-level rather than
 * whole-list, so a push doesn't cost the user their scroll position, their
 * selection, or an in-progress edit.
 */
export interface RyPatch {
  type: 'insert' | 'update' | 'delete';
  row: RyRecord;
}

/**
 * What a subscribing source calls back into.
 *
 * `closed` is what makes reconnect possible: without a way to say "the socket
 * dropped", a widget can only sit there looking connected. `resume` lets the
 * source hand back a token so a reconnect can continue rather than replay.
 */
export interface RySubscribeHandlers {
  /** A row-level change arrived. */
  patch(patch: RyPatch): void;
  /** The connection ended. Pass an error for an unclean close. */
  closed(error?: unknown): void;
  /** Record a resume token, sent back as `RyRequest.cursor` on reconnect. */
  resume?(token: string): void;
}

/** A source that pushes patches. Handlers are supplied by the consumer. */
export type RySubscribeSource = (
  req: RyRequest,
  handlers: RySubscribeHandlers
) => RySubscription;

/** A source that yields chunks over time. */
export type RyStreamSource = (req: RyRequest) => AsyncIterable<RyChunk>;

/** The one required function. Everything else in the contract is optional. */
export type RySource = (req: RyRequest) => RyResponse | Promise<RyResponse>;

/** Any of the shapes a provider can implement. */
export type RyAnySource = RySource | RyStreamSource | RySubscribeSource;

/** Where a moved record lands. Matches ry-tree's shipped `ry:move` vocabulary. */
export interface RyMovePosition {
  position: 'before' | 'after' | 'inside';
  target: unknown;
}

/**
 * Writes. SPECIFIED, NOT IMPLEMENTED — no widget mutates yet (table and chart
 * are read-only, ry-tree is DOM-as-truth). This exists so the first widget that
 * writes adopts it instead of inventing its own vocabulary.
 */
export interface RyMutations {
  create?(row: RyRecord, req: RyRequest): Promise<RyRecord>;
  update?(id: unknown, patch: RyRecord, req: RyRequest): Promise<RyRecord>;
  remove?(id: unknown, req: RyRequest): Promise<void>;
  /**
   * Reorder / reparent. NOT an update — a position change carries intent that a
   * field diff can't recover.
   */
  move?(id: unknown, to: RyMovePosition, req: RyRequest): Promise<void>;
}

/**
 * Lifecycle state a source-backed host reflects via `data-ry-state`.
 *
 * `loading` and `refreshing` are deliberately distinct: `loading` means nothing
 * is on screen yet, so render a skeleton; `refreshing` means rows are already
 * there and should stay. Collapsing them makes blanking the table on every sort
 * click the likely default.
 *
 *   idle → loading → ready | empty | error
 *   ready → refreshing → ready | empty | error
 */
export type RyDataState = 'idle' | 'loading' | 'refreshing' | 'ready' | 'empty' | 'error';

export interface RyLoadDetail { request: RyRequest }
export interface RyLoadedDetail extends RyResponse { request: RyRequest }
/** NOTE: the event is `ry:load-error`, not `ry:error` — ry-copy-button owns that
 *  name and a copy button inside a table cell would bubble it straight through. */
export interface RyErrorDetail { error: unknown; request: RyRequest }

export interface RyChunkDetail { chunk: RyChunk; rows: ReadonlyArray<RyRecord> }
export interface RyPatchDetail { patch: RyPatch; rows: ReadonlyArray<RyRecord> }
export interface RyMutateErrorDetail { error: unknown }

declare global {
  interface HTMLElementEventMap {
    'ry:load': CustomEvent<RyLoadDetail>;
    'ry:loaded': CustomEvent<RyLoadedDetail>;
    'ry:load-error': CustomEvent<RyErrorDetail>;
    'ry:chunk': CustomEvent<RyChunkDetail>;
    'ry:patch': CustomEvent<RyPatchDetail>;
    'ry:mutate-error': CustomEvent<RyMutateErrorDetail>;
  }
}

/**
 * A stable identity for a request's SEMANTIC content — everything that affects
 * which records come back, and nothing else.
 *
 * `signal` is excluded: two widgets asking the identical question with different
 * abort controllers are asking the same question. Object keys are sorted so
 * `{a:1,b:2}` and `{b:2,a:1}` agree; `sort` is an array and its order is
 * preserved, because most-significant-first is meaning, not formatting.
 *
 * This is the unlock for dedupe, caching, and shared results. It is pure and has
 * no imports, so it stays part of the contract rather than an implementation
 * detail of whoever needs it first.
 */
export function requestKey(req: RyRequest): string {
  return stableStringify({
    range: req.range ? [req.range.start, req.range.end] : null,
    cursor: req.cursor ?? null,
    sort: req.sort?.length ? req.sort.map((s) => [s.field, s.dir]) : null,
    filter: req.filter ?? null,
    query: req.query ?? null,
    meta: req.meta ?? null,
  });
}

/** JSON.stringify with object keys sorted at every depth. Arrays keep their order. */
function stableStringify(value: unknown): string {
  if (value === null || value === undefined) return 'null';
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(',')}]`;
  if (typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>)
      .filter(([, v]) => v !== undefined)
      .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
      .map(([k, v]) => `${JSON.stringify(k)}:${stableStringify(v)}`);
    return `{${entries.join(',')}}`;
  }
  return JSON.stringify(value);
}

/**
 * A request that ran out of time.
 *
 * A timeout is NOT a plain abort. Superseding a request is normal operation and
 * stays silent; running out of time is a failure the user needs to see. They
 * both travel as aborts at the transport level, so they need distinguishing at
 * this one.
 */
export class RyTimeoutError extends Error {
  readonly timeoutMs: number;
  constructor(timeoutMs: number) {
    super(`ry-ui: request timed out after ${timeoutMs}ms`);
    this.name = 'RyTimeoutError';
    this.timeoutMs = timeoutMs;
  }
}

/** A non-2xx response. Carries `status` so retry can tell "try again" from "don't". */
export class RyHttpError extends Error {
  readonly status: number;
  constructor(status: number, statusText: string, url: string) {
    super(`${status} ${statusText} — ${url}`);
    this.name = 'RyHttpError';
    this.status = status;
  }
}

/**
 * Should this failure be retried?
 *
 * A 404 will be a 404 next time; retrying it wastes a round trip and delays the
 * error the user needs to see. A 503, a 429, or a dropped connection are all
 * plausibly transient. Timeouts are retryable — that's the case retry exists for.
 */
export function isRetryable(error: unknown): boolean {
  if (error instanceof RyTimeoutError) return true;
  if (error instanceof RyHttpError) {
    return error.status >= 500 || error.status === 408 || error.status === 429;
  }
  // A thrown TypeError from fetch is the network being down.
  return !isAbort(error);
}

/** True when `err` is an abort rather than a real failure. Aborts are not errors. */
export function isAbort(err: unknown): boolean {
  if (err instanceof DOMException && err.name === 'AbortError') return true;
  // Some runtimes / hand-rolled sources reject with a plain object.
  return typeof err === 'object' && err !== null && (err as { name?: string }).name === 'AbortError';
}
